import { memo, ReactElement, useCallback, useMemo } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FeeProfile } from '@pmp/api';
import { useTableView } from '@pmp/hooks';
import { Guard, useGuard } from '@ohpen/react-authz';
import { lockActionTitle } from '@pmp/utils';
import { ListProps } from '@pmp/types';
import { useExportTemplatesStore } from '@pmp/stores';

import { feeProfilesColumns, feeProfilesExportColumns } from '../tableConfig';

const entityType = ActionEntityType.FEE_PROFILES;
const rowKey = 'feeProfileId';

export const FeeProfilesList = memo(
  ({ data, isFetching, valueLists, onCreate, onView, onEdit, onDelete }: ListProps<FeeProfile>): ReactElement => {
    const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });

    const { t } = useTranslation();

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.FEE_PROFILES), [getExportTemplates]);

    const getRowActions: (record: FeeProfile) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View Fee Profile" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['feeProfiles:update']} flags={['enableFeeProfilesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit Fee Profile"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['feeProfiles:delete']} flags={['enableFeeProfilesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete Fee Profile"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t],
    );

    const tableProps: TableProps<ActionEntityType, FeeProfile, unknown, unknown> = useMemo(
      () => ({
        id: entityType,
        dataSource: data ?? [],
        columns: feeProfilesColumns,
        exportColumns: feeProfilesExportColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        loading: isFetching,
        getRowActions,
        valueLists,
        showRowSelection,
        showViews: true,
        tableViews,
        saveTableView,
        deleteTableView,
      }),
      [data, isFetching, getRowActions, valueLists, showRowSelection, tableViews, saveTableView, deleteTableView],
    );

    return (
      <Table {...tableProps}>
        <>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Fee Profiles ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Fee Profiles"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  exportView
                />
              </Guard>
              <Guard permissions={['feeProfiles:create']} flags={['enableFeeProfilesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="pmp.fees.labels.xFeeProfilesSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`Fee Profiles ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Fee Profiles"
                disabled={isFetching}
                loadingTemplates={isLoadingExportTemplates}
                getTemplates={getTemplates}
                modalDefaults={{ columnScope: 'template' }}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </>
      </Table>
    );
  },
);
